import { dateToInputValue } from 'common/date.ts';
import dayjs from 'common/dayjs.ts';
import { getRuntimeTimezone, MPUT, truncateTimezone } from 'common/helpers.ts';
import { signatorEmptyOrValid } from 'common/validators.ts';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWRMutation from 'swr/mutation';
import type { Leg } from 'types/Leg.ts';
import { Button } from 'ui/component/Button.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Input } from 'ui/control/Input.tsx';
import { TextArea } from 'ui/control/TextArea.js';
import { getTranslatedError } from '#admin/helpers.ts';

type Props = {
  leg: Leg;
  onSave?: () => void;
  onClose?: () => void;
  isOpen: boolean;
};

type FormValues = {
  origin_signer_name: string;
  destination_signer_name: string;
  origin_comments: string;
  destination_comments: string;
  origin_actual_datetime: string;
  destination_actual_datetime: string;
  origin_contact_name: string;
  origin_contact_number: string;
  destination_contact_name: string;
  destination_contact_number: string;
  instructions: string;
};

export const LegEditDialog = ({ leg, onSave, onClose, isOpen }: Props) => {
  const { t } = useTranslation();
  const updateLegMutation = useSWRMutation(`/leg/${leg.id}`, MPUT);
  const isLoading = updateLegMutation.isMutating;

  const pendingPOC = leg.poc_required && !leg.poc_url;
  const collectedByDriver = leg.origin_actual_datetime && !leg.origin_manual_actual_datetime;
  const canCollect = !pendingPOC && !collectedByDriver;

  const pendingPOD = leg.pod_required && !leg.pod_url;
  const deliveredByDriver = leg.destination_actual_datetime && !leg.destination_manual_actual_datetime;
  const canDeliver = !pendingPOD && !deliveredByDriver;
  const origin_tz = leg.origin_tz || getRuntimeTimezone();
  const destination_tz = leg.destination_tz || getRuntimeTimezone();

  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: false, // we need the default values to be applied in all cases
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      origin_signer_name: leg.origin_signer_name || '',
      destination_signer_name: leg.destination_signer_name || '',
      origin_comments: leg.origin_comments || '',
      destination_comments: leg.destination_comments || '',
      origin_actual_datetime: dateToInputValue(leg.origin_actual_datetime, origin_tz) || '',
      destination_actual_datetime: dateToInputValue(leg.destination_actual_datetime, destination_tz) || '',
      origin_contact_name: leg.origin_contact_name || '',
      origin_contact_number: leg.origin_contact_number || '',
      destination_contact_name: leg.destination_contact_name || '',
      destination_contact_number: leg.destination_contact_number || '',
      instructions: leg.instructions || '',
    },
  });

  const { handleSubmit, formState } = formMethods;

  const onSubmit = async (values: FormValues) => {
    const current_origin_actual_datetime = leg.origin_actual_datetime ? dayjs(leg.origin_actual_datetime).set('seconds', 0).set('milliseconds', 0).toISOString() : null;

    const new_origin_actual_datetime = values.origin_actual_datetime
      ? dayjs.tz(values.origin_actual_datetime, origin_tz).set('seconds', 0).set('milliseconds', 0).toISOString()
      : null;

    const current_destination_actual_datetime = leg.destination_actual_datetime
      ? dayjs(leg.destination_actual_datetime).set('seconds', 0).set('milliseconds', 0).toISOString()
      : null;

    const new_destination_actual_datetime = values.destination_actual_datetime
      ? dayjs.tz(values.destination_actual_datetime, destination_tz).set('seconds', 0).set('milliseconds', 0).toISOString()
      : null;

    const origin_was_changed = current_origin_actual_datetime !== new_origin_actual_datetime;
    const destination_was_changed = current_destination_actual_datetime !== new_destination_actual_datetime;

    if (!formState.isDirty) return;

    const data = {
      origin_actual_datetime: origin_was_changed ? new_origin_actual_datetime : undefined,
      destination_actual_datetime: destination_was_changed ? new_destination_actual_datetime : undefined,
      origin_manual_actual_datetime: origin_was_changed || undefined,
      destination_manual_actual_datetime: destination_was_changed || undefined,
      origin_signer_name: values.origin_signer_name || undefined,
      destination_signer_name: values.destination_signer_name || undefined,
      origin_comments: values.origin_comments || undefined,
      destination_comments: values.destination_comments || undefined,
      origin_contact_name: values.origin_contact_name || null,
      origin_contact_number: values.origin_contact_number || null,
      destination_contact_name: values.destination_contact_name || null,
      destination_contact_number: values.destination_contact_number || null,
      instructions: values.instructions || null,
    };

    await updateLegMutation.trigger(data);
    onSave?.();
  };

  return (
    <Modal key={leg.id} title={<h4>{t('dialogs.leg.title')}</h4>} isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <form className="flex flex-col gap-2.5 overflow-y-scroll p-2" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-row gap-2">
            <span>{t('labels.legId')}:</span>
            <span>{leg.logistics_ex_id}</span>
          </div>

          <div className="grid w-[550px] grid-cols-2 grid-rows-4 gap-x-4">
            <div className="mt-6 flex flex-col">
              <Input
                className="w-full"
                name="origin_actual_datetime"
                label={`${t('dialogs.leg.fields.actualCollectionDatetime')} (${truncateTimezone(origin_tz)})`}
                type="datetime-local"
                disabled={isLoading || !canCollect}
              />
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <Input
                name="origin_signer_name"
                label={t('dialogs.leg.fields.originSignerName')}
                disabled={isLoading || !canCollect}
                registerOptions={{ required: canCollect && leg.poc_required, validate: { signatorEmptyOrValid } }}
              >
                {formState.errors.origin_signer_name?.type === 'required' && t('dialogs.leg.errors.required')}
              </Input>
            </div>

            <div className="col-span-2 mt-6 flex flex-col gap-2">
              <TextArea name="origin_comments" label={t('dialogs.leg.fields.originComments')} disabled={isLoading || !canCollect} maxLength={250}>
                {formState.errors.origin_comments?.type === 'required' && t('dialogs.leg.errors.required')}
              </TextArea>
            </div>

            <div className="mt-6 flex flex-col">
              <Input
                name="destination_actual_datetime"
                label={`${t('dialogs.leg.fields.actualDeliveryDatetime')} (${truncateTimezone(destination_tz)})`}
                type="datetime-local"
                disabled={isLoading || !canDeliver}
              />
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <Input
                name="destination_signer_name"
                label={t('dialogs.leg.fields.destinationSignerName')}
                disabled={isLoading || !canDeliver}
                registerOptions={{ required: canDeliver && leg.pod_required, validate: { signatorEmptyOrValid } }}
              >
                {formState.errors.destination_signer_name?.type === 'required' && t('dialogs.leg.errors.required')}
              </Input>
            </div>

            <div className="col-span-2 mt-6 flex flex-col gap-2">
              <TextArea name="destination_comments" label={t('dialogs.leg.fields.destinationComments')} disabled={isLoading || !canDeliver} maxLength={250}>
                {formState.errors.destination_comments?.type === 'required' && t('dialogs.leg.errors.required')}
              </TextArea>
            </div>

            {pendingPOC && <p className="col-span-2">{t('views.shipment.pocDisabled')}</p>}
            {pendingPOD && <p className="col-span-2">{t('views.shipment.podDisabled')}</p>}

            <div className="col-span-2 mt-6">
              <hr />
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <Input name="origin_contact_name" label={t('dialogs.leg.fields.originContactName')} disabled={isLoading} />
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <Input name="origin_contact_number" label={t('dialogs.leg.fields.originContactNumber')} disabled={isLoading} />
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <Input name="destination_contact_name" label={t('dialogs.leg.fields.destinationContactName')} disabled={isLoading} />
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <Input name="destination_contact_number" label={t('dialogs.leg.fields.destinationContactNumber')} disabled={isLoading} />
            </div>

            <div className="col-span-2 mt-6">
              <hr />
            </div>

            <div className="col-span-2 mt-6 flex flex-col gap-2">
              <TextArea name="instructions" label={t('dialogs.leg.fields.instructions')} disabled={isLoading} />
            </div>
          </div>

          <div className="mt-4 flex gap-5">
            <Button type="button" disabled={isLoading} className="blue-outlined" onClick={onClose}>
              {t('actions.back')}
            </Button>
            <Button type="submit" disabled={!formState.isValid || !formState.isDirty} loading={isLoading} className="blue flex items-center justify-center">
              {t('actions.save')}
            </Button>
          </div>
          {updateLegMutation.error && (
            <div className="red mt-2">{getTranslatedError(updateLegMutation.error, t) || 'An error occurred and your request could not be completed.'}</div>
          )}
        </form>
      </FormProvider>
    </Modal>
  );
};
